import qs from 'qs';

import type {
  CharterPricingInventoryApiRow,
  CharterPricingInventoryRow,
  ListCharterPricingInventoryParams,
  ListCharterPricingInventoryResponse,
  UpsertCharterPricingInventoryBody,
  UpsertCharterPricingInventoryResponse,
} from '~/types/charterPricingInventory';

import { json_headers, request } from './common';

const BASE_PATH = `${window.configs.API_HOST}/api/flights-charter/v1/fare-families/fnf`;

type RequestOptions = {
  signal?: AbortSignal;
};

type CharterPricingInventoryApiResponse = {
  status: number;
  message: string;
  result: Array<CharterPricingInventoryApiRow>;
  pagination?: {
    limit: number;
    offset: number;
    totalItems?: number;
    total_items?: number;
    hasNext?: boolean;
    has_next?: boolean;
  };
};

const contractToUiCabinMap = {
  ECONOMY: 'economy',
  PREMIUM_ECONOMY: 'premium_economy',
  LUX_ROW: 'lux_row',
  LUX_ROW_PAX_ONE: 'lux_row_pax_one',
  BUSINESS: 'business',
} as const;

const uiToContractCabinMap = {
  economy: 'ECONOMY',
  premium_economy: 'PREMIUM_ECONOMY',
  lux_row: 'LUX_ROW',
  lux_row_pax_one: 'LUX_ROW_PAX_ONE',
  business: 'BUSINESS',
} as const;

function toUiCabinClass(cabinClass: string): CharterPricingInventoryRow['cabin'] {
  return contractToUiCabinMap[cabinClass as keyof typeof contractToUiCabinMap] ?? 'economy';
}

export function toContractCabinClass(
  cabinClass: CharterPricingInventoryRow['cabin'],
): UpsertCharterPricingInventoryBody['cabinClass'] {
  return uiToContractCabinMap[cabinClass];
}

function getPricingPrice(
  pricing: Array<CharterPricingInventoryApiRow['pricing'][number]>,
  paxType: 'ADT' | 'CHD' | 'INF',
): number {
  return pricing.find((pricingRow) => pricingRow.paxType === paxType)?.price ?? 0;
}

function toCharterPricingInventoryRow(pricingInventory: CharterPricingInventoryApiRow): CharterPricingInventoryRow {
  const depAirport = pricingInventory.flightInstance?.depAirport ?? '';
  const arrAirport = pricingInventory.flightInstance?.arrAirport ?? '';

  return {
    id: pricingInventory.fnfFareId,
    flightInstanceId: pricingInventory.flightInstanceId,
    depAirport,
    arrAirport,
    route: depAirport && arrAirport ? `${depAirport} -> ${arrAirport}` : '',
    date: pricingInventory.flightInstance?.depDate ?? '',
    cabin: toUiCabinClass(pricingInventory.cabinClass),
    availableDaysBefore: pricingInventory.availableDaysBefore,
    promoCodeDaysBefore: pricingInventory.promoCodeDaysBefore,
    waitlistSeat: pricingInventory.allocation,
    totalBooked: pricingInventory.booked,
    adultPrice: getPricingPrice(pricingInventory.pricing, 'ADT'),
    childPrice: getPricingPrice(pricingInventory.pricing, 'CHD'),
    infantPrice: getPricingPrice(pricingInventory.pricing, 'INF'),
    employee: pricingInventory.bookedEmployee,
    employeeWithPromoCode: pricingInventory.bookedEmployeeWithPromoCode,
    friendAndFamily: pricingInventory.bookedReferral,
    openForSale: pricingInventory.allocation - pricingInventory.booked,
  };
}

export async function listCharterPricingInventory(
  params: ListCharterPricingInventoryParams = {},
  options: RequestOptions = {},
): Promise<ListCharterPricingInventoryResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  const url = query ? `${BASE_PATH}?${query}` : BASE_PATH;

  const response: CharterPricingInventoryApiResponse = await request(url, {
    ...options,
    method: 'GET',
  });

  return {
    status: response.status,
    message: response.message,
    result: response.result.map((pricingInventory) => toCharterPricingInventoryRow(pricingInventory)),
    pagination: response.pagination
      ? (() => {
          const totalItems =
            response.pagination.totalItems ?? response.pagination.total_items ?? response.result.length;
          return {
            limit: response.pagination.limit,
            offset: response.pagination.offset,
            totalItems,
            hasNext:
              response.pagination.hasNext ??
              response.pagination.has_next ??
              response.pagination.offset + response.pagination.limit < totalItems,
          };
        })()
      : undefined,
  };
}

export async function fetchAllCharterPricingInventory(
  params: Omit<ListCharterPricingInventoryParams, 'limit' | 'offset'> = {},
): Promise<Array<CharterPricingInventoryRow>> {
  const pageSize = 1000;
  const allRows: Array<CharterPricingInventoryRow> = [];
  let offset = 0;
  let hasNextPage = true;

  while (hasNextPage) {
    const response = await listCharterPricingInventory({ ...params, offset, limit: pageSize });
    allRows.push(...response.result);

    // The original endpoint was unpaginated. Preserve that compatibility while
    // following pagination metadata when the API supplies it.
    if (!response.pagination || !response.pagination.hasNext || response.result.length === 0) {
      hasNextPage = false;
      continue;
    }

    offset += response.pagination.limit;
  }

  return allRows;
}

export async function upsertCharterPricingInventory(
  body: UpsertCharterPricingInventoryBody,
): Promise<UpsertCharterPricingInventoryResponse> {
  const response: { status: number; message: string; result: CharterPricingInventoryApiRow } = await request(
    BASE_PATH,
    {
      method: 'PUT',
      headers: json_headers,
      body: JSON.stringify(body),
    },
  );

  return {
    status: response.status,
    message: response.message,
    result: toCharterPricingInventoryRow(response.result),
  };
}
